import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { ArrowRight } from "lucide-react"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coderide-light-blue focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Primary: Solid CodeRide brand button
        primary: "bg-coderide-light-blue text-[#050505] hover:bg-coderide-dark-blue hover:text-white",
        // Secondary: Outlined CodeRide brand button
        secondary: "border border-coderide-light-blue text-coderide-light-blue bg-transparent hover:bg-coderide-light-blue hover:text-[#050505]",
        // Tertiary: Text with underline
        tertiary: "text-coderide-light-blue hover:text-coderide-dark-blue underline underline-offset-4 hover:underline-offset-2 bg-transparent border-none p-0 h-auto",
        // Legacy variants for backward compatibility
        default: "bg-coderide-light-blue text-white hover:bg-coderide-dark-blue",
        outline: "border-2 border-coderide-light-blue text-coderide-light-blue bg-transparent hover:bg-coderide-light-blue hover:text-white",
        ghost: "text-coderide-light-blue hover:bg-coderide-light-blue/10",
        link: "text-coderide-light-blue underline-offset-4 hover:underline",
      },
      size: {
        default: "h-12 px-6 py-3 text-base",
        sm: "h-10 px-4 py-2 text-sm",
        lg: "h-14 px-8 py-4 text-lg",
        icon: "h-12 w-12",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  showArrow?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, showArrow = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    
    const content = showArrow ? (
      <>
        {children}
        <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
      </>
    ) : children

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }), showArrow && "group")}
        ref={ref}
        {...props}
      >
        {content}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
